"use client";

import { createContext, useContext, useState } from "react";
import type { AppearancePreference, WebsiteAppearance } from "@/cms/appearance";

interface AppearanceContextValue {
  settings: WebsiteAppearance;
  preference: AppearancePreference;
  update: (preference: Pick<AppearancePreference, "scheme">) => void;
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null);

export function useWebsiteAppearance() {
  return useContext(AppearanceContext);
}

export default function AppearanceProvider({
  settings,
  initial,
  cookieName,
  children,
}: {
  settings: WebsiteAppearance;
  initial: AppearancePreference;
  cookieName: string;
  children: React.ReactNode;
}) {
  const [scheme, setScheme] = useState(initial.scheme);
  const preference = { scheme, shape: settings.defaultShape ?? "rounded" };
  function update(next: Pick<AppearancePreference, "scheme">) {
    if (!settings.allowSchemeSwitch || !settings.palettes?.[next.scheme])
      return;
    setScheme(next.scheme);
    document.documentElement.dataset.reScheme = next.scheme;
    document.cookie = `${cookieName}=${encodeURIComponent(JSON.stringify({ scheme: next.scheme }))}; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  }
  return (
    <AppearanceContext.Provider value={{ settings, preference, update }}>
      {children}
    </AppearanceContext.Provider>
  );
}
